import { type SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { tv } from "tailwind-variants";

import { useToastStore } from "@/components";
import { DISCOUNT_CODE_ACTIONS, NO_DISCOUNT_CODE } from "@/domains/careplan/models/constants";
import { toggleDiscountCodeFormSchema } from "@/domains/careplan/models/schemas";
import type {
  CareplanCartDataModel,
  ToggleDiscountCodeForm,
} from "@/domains/careplan/models/types";
import { useUpdateCareplanCartDiscount } from "@/domains/careplan/mutations/use-update-careplan-cart-discount";
import { env } from "@/env";
import { Button, Input } from "@/ui";

const discountToggleVariants = tv({
  slots: {
    discountToggle: "rounded-lg font-medium",
  },
  variants: {
    applied: {
      true: {
        discountToggle:
          "bg-surface-sunken text-content-secondary hover:bg-surface-sunken focus:bg-surface-sunken",
      },
    },
  },
});

const { discountToggle } = discountToggleVariants();

export const CareplanCartDiscountCodeForm = ({
  cart,
}: {
  cart: NonNullable<CareplanCartDataModel>;
}) => {
  const {
    formState: { errors, isValid },
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
  } = useForm<ToggleDiscountCodeForm>({
    defaultValues: {
      action: DISCOUNT_CODE_ACTIONS.APPLY,
    },
    mode: "onChange",
    resolver: zodResolver(toggleDiscountCodeFormSchema),
  });

  const pushToast = useToastStore((state) => {
    return state.pushToast;
  });

  const isApplied = watch("action") === DISCOUNT_CODE_ACTIONS.REMOVE;

  const { isPending, mutate: updateCartDiscount } = useUpdateCareplanCartDiscount();

  const handleApplyCode = (code: string, currentCodes: string[]) => {
    updateCartDiscount(
      {
        cartId: cart.id,
        discountCodes: [...new Set([...currentCodes, code, env.VITE_CAREPLAN_DISCOUNT_CODE])],
      },
      {
        onSuccess: () => {
          setValue("action", DISCOUNT_CODE_ACTIONS.REMOVE);
          pushToast({
            type: "success",
            title: "Discount code applied successfully",
          });
        },
        onError: () => {
          pushToast({
            type: "error",
            title: "Failed to apply discount code",
          });
        },
      },
    );
  };

  const handleRemoveCode = (code: string, currentCodes: string[]) => {
    const removedDiscountCodes = currentCodes.filter((currentCode) => {
      return currentCode !== code;
    });
    updateCartDiscount(
      {
        cartId: cart.id,
        discountCodes: [...new Set([...removedDiscountCodes, env.VITE_CAREPLAN_DISCOUNT_CODE])],
      },
      {
        onSuccess: () => {
          reset();
          pushToast({
            type: "success",
            title: "Discount code removed successfully",
          });
        },
        onError: () => {
          pushToast({
            type: "error",
            title: "Failed to remove discount code",
          });
        },
      },
    );
  };

  const onSubmit: SubmitHandler<ToggleDiscountCodeForm> = ({ action, code }) => {
    const currentDiscountCodes = cart.discounts
      .map((discount) => {
        return discount.code;
      })
      .filter((discountCode): discountCode is string => {
        return Boolean(discountCode) && discountCode !== NO_DISCOUNT_CODE;
      });

    if (action === DISCOUNT_CODE_ACTIONS.APPLY) {
      handleApplyCode(code, currentDiscountCodes);
    } else {
      handleRemoveCode(code, currentDiscountCodes);
    }
  };

  return (
    <form className="flex flex-col gap-2" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex w-full items-end gap-2">
        <Input
          className="rounded-lg border-line"
          containerClassName="flex-1"
          disabled={isApplied || isPending}
          label="Discount Code"
          placeholder="Enter code"
          {...register("code")}
        />
        <Button
          className={discountToggle({ applied: isApplied })}
          disabled={!isValid || isPending}
          isLoading={isPending}
          type="submit"
        >
          {isApplied ? "Remove" : "Apply"}
        </Button>
      </div>
      {errors.code ? (
        <p className="text-sm text-danger">{errors.code.message}</p>
      ) : (
        <p className="text-sm text-content-muted">
          Applies to next order only, not recurring subscription
        </p>
      )}
    </form>
  );
};
